import { Dispatch } from 'redux';
import backend from '../../../apis/backend';
import { ReduxState } from '../../state';
import { updatePageOrderInputs } from '../actions';
import { PageOrderFlow } from '../types';

// Thunk action to calculate fees
export const asyncCalculateFees = () => {
  return async (dispatch: Dispatch, getState: () => ReduxState) => {
    const { pageOrderFlow, selectedOrderItem, selectedPage } = getState().page;
    try {
      const { loginToken } = getState().auth;
      const { selectedLocation } = getState().location;
      let businessId = null;
      let defaultBusinessLocationId = null;
      const pageData = getState().page.selectedPage?.pageData;
      const associatedBusinesses = getState().page.selectedPage?.associatedBusinesses;
      const leadTimeHours = selectedPage?.pageData?.leadTimeHours || 24;
      const deliveryDiscount = selectedPage?.pageData?.deliveryDiscount;
      const deliveryDiscountMode = selectedPage?.pageData?.deliveryDiscountMode;
      const discount = selectedPage?.pageData?.discount;
      const discountMode = selectedPage?.pageData?.discountMode;
      const discountName = selectedPage?.pageData?.discountName;
      if (pageData && pageData.defaultLocationId) {
        defaultBusinessLocationId = pageData.defaultLocationId;
      }
      if (associatedBusinesses && associatedBusinesses.length) {
        businessId = associatedBusinesses[0]._id;
      }

      const destinationLatitude = selectedLocation?.latitude;
      const destinationLongitude = selectedLocation?.longitude;

      const headers = {
        'X-Auth-Token': loginToken,
      };     
      const data = {
        destinationLatitude,
        destinationLongitude,
        defaultBusinessLocationId,
        businessId
      } 
      const response = await backend.post(
        `/orchestrator-service/api/v1/calculators/best-location-delivery-fee`, 
        data, { headers });

      if (response.status === 200 && selectedOrderItem && destinationLatitude && destinationLongitude) {
        // gather the info, make the calculations and update the page order flow
        // account for pagesTransactionPercentageRate and deliveryDiscount
        // only allow local, interbranch, out of zone - no out of range
        // max distance is 50km if out of zone
        const { businessLocation, deliveryFeeCalculation } = response.data;
        let discountItems: any = [];
        if (deliveryDiscount && deliveryDiscountMode) {
          let price = 0;
          if (deliveryDiscountMode === 'amount') price = -1 * deliveryDiscount;
          if (deliveryDiscountMode === 'percent') price = -1 * deliveryFeeCalculation.deliveryFee * deliveryDiscount / 100;
          discountItems = [
            ...discountItems,
            {
              _id: 'delivery-discount',
              price,
              name: 'Delivery Discount',
              status: 'active',
              currency: selectedOrderItem.currency,
              quantity: 1,
              data: {
                details: "", addOns: []
              }
            }
          ];
        }
        if (discount && discountMode) {
          let price = 0;
          if (discountMode === 'amount') price = -1 * discount;
          if (discountMode === 'percent') price = -1 * selectedOrderItem.price * selectedOrderItem.quantity * discount / 100;
          discountItems = [
            ...discountItems,
            {
              _id: 'item-discount',
              price,
              name: `${discountName} Discount`,
              status: 'active',
              currency: selectedOrderItem.currency,
              quantity: 1,
              data: {
                details: "", addOns: []
              }
            }
          ];
        }
        const orderItems = [
          selectedOrderItem,
          ...discountItems
        ];


        const { pricing } = deliveryFeeCalculation.billingBranch;
        const orderExpenses = orderItems.reduce((total, orderItem) => {
          return total + (orderItem.price * orderItem.quantity);
        }, 0);
        const pagesTransactionFee = pricing.pagesTransactionFeePercentageRate * orderExpenses;
        const deliveryFee = deliveryFeeCalculation.deliveryFee;
        const tax = deliveryFee * 15/115
        const orderRevenue = orderExpenses + deliveryFee;


        const updatedPageOrderFlow: PageOrderFlow = {
          ...pageOrderFlow,
          sourceId: businessLocation._id,
          destinationId: selectedLocation._id,
          distance: deliveryFeeCalculation.drivingDistance,
          deliveryFee,
          deliveryCommission: deliveryFeeCalculation.commission,
          orderExpenses,
          pagesTransactionFee,
          orderRevenue,
          tax,
          currency: selectedOrderItem.currency,
          // discountAmount - calculate based on delivery discount
          // discountDescription - calculate based on delivery discount
          pointsEarned: Math.min(Math.floor(deliveryFeeCalculation.drivingDistance), 100),
          deliveryType: deliveryFeeCalculation.deliveryType,
          originBranchCode: deliveryFeeCalculation.originBranch.branchCode,
          branchCode: deliveryFeeCalculation.billingBranch.branchCode,
          calculatingDeliveryFee: false,
          data: {
            ...pageOrderFlow.data,
            destinationLatitude,
            destinationLongitude,
            orderItems,
          }
        };
        updatedPageOrderFlow.data.purchaseMethods = [
          {
            name: 'invoice',
            amount: updatedPageOrderFlow.orderExpenses
          }
        ]
        updatedPageOrderFlow.data.sourceLatitude = businessLocation.latitude;
        updatedPageOrderFlow.data.sourceLongitude = businessLocation.longitude;
        const target = new Date();
        target.setHours(target.getHours() + 1 + leadTimeHours);
        const minutes = target.getMinutes();
        const remainder = minutes % 15;
        if (remainder !== 0) {
          // Round up to the nearest 15 minutes
          target.setMinutes(minutes + (15 - remainder));
        }
        updatedPageOrderFlow.targetDeliveryTime = target.toISOString()
        dispatch(updatePageOrderInputs(updatedPageOrderFlow));
      }
    } catch (error) {
      console.error('Error calculating fees', error);
      dispatch(updatePageOrderInputs({...pageOrderFlow, calculatingDeliveryFee: false}));
    }
  };
};